import { formatRisk, round2 } from "./format";

const WAGER_PERCENT_PRECISION = 10000;

/**
 * Converts a risk field value to the percent of the wager the backend expects.
 * @param {number|string} value
 * @param {'$' | '%'} mode
 * @param {number|string} wager
 * @returns {number|null} null when the field is empty
 */
export const toWagerPercent = (value, mode, wager) => {
  const amount = Number(value) || 0;

  if (amount <= 0) return null;
  if (mode === "%") return amount;

  const margin = Number(wager) || 0;

  if (margin <= 0) return null;

  return (
    Math.round((amount / margin) * 100 * WAGER_PERCENT_PRECISION) /
    WAGER_PERCENT_PRECISION
  );
};

/**
 * Converts TP/SL between $ and % using margin (wager).
 * @param {number} value
 * @param {'$' | '%'} nextMode
 * @param {number|string} wager
 * @returns {number}
 */
export const convertRiskValue = (value, nextMode, wager) => {
  const margin = Number(wager) || 0;

  if (nextMode === "%") {
    if (margin <= 0) return 0;

    return round2((value / margin) * 100);
  }

  return round2(margin * (value / 100));
};

/**
 * @param {number} value
 * @returns {{ value: number, draft: string }}
 */
export const normalizeRiskField = (value) => {
  const rounded = round2(value);

  return {
    value: rounded,
    draft: formatRisk(rounded),
  };
};

/**
 * Upper bound for SL: 100% of wager in % mode, or the wager itself in $ mode.
 * @param {'$' | '%'} mode
 * @param {number|string} wager
 * @returns {number}
 */
export const maxRiskValue = (mode, wager) => {
  if (mode === "%") return 100;

  return round2(Math.max(Number(wager) || 0, 0));
};

const TP_MIN_PERCENT = 1;
const TP_MAX_PERCENT = 5000;

/**
 * Validates TP/SL values against business rules.
 * Returns { titleKey, textKey, textParams } or null if valid.
 * @param {object} params
 * @param {'$' | '%'} params.mode
 * @param {number} params.tpValue
 * @param {number} params.slValue
 * @param {number|string} params.wager
 * @returns {{ titleKey: string, textKey: string, textParams?: object } | null}
 */
export const getRiskValidationError = ({ mode, tpValue, slValue, wager }) => {
  const margin = Number(wager) || 0;
  const slMax = maxRiskValue(mode, margin);

  if (slValue > slMax && slValue > 0) {
    return {
      titleKey: "futures.new_validation_tp_sl.error_title",
      textKey:
        mode === "%"
          ? "futures.new_validation_tp_sl.sl_over_limit_percent"
          : "futures.new_validation_tp_sl.sl_over_limit_dollar",
    };
  }

  if (tpValue > 0) {
    let tpMin, tpMax;

    if (mode === "%") {
      tpMin = TP_MIN_PERCENT;
      tpMax = TP_MAX_PERCENT;
    } else {
      // Up to a whole cent, so the minimum isn't below TP_MIN_PERCENT of the wager
      tpMin = Math.ceil(margin * TP_MIN_PERCENT) / 100;
      tpMax = round2(margin * (TP_MAX_PERCENT / 100));
    }

    if (tpValue < tpMin || tpValue > tpMax) {
      return {
        titleKey: "futures.new_validation_tp_sl.error_title",
        textKey: "futures.new_validation_tp_sl.tp_out_of_range",
        textParams: { min: tpMin, max: tpMax, mode },
      };
    }
  }

  return null;
};
